import { useWhitelabel } from "../context/WhitelabelContext";
import { WhitelabelSettings } from "../context/WhitelabelContext";

/**
 * Get the appropriate employee terminology based on whitelabel settings
 * Falls back to default "Employee"/"Employees" if not customized
 */
export const getEmployeeTerminology = (
  whitelabelSettings: WhitelabelSettings | null,
  plural: boolean = false
): string => {
  if (!whitelabelSettings) {
    return plural ? "Employees" : "Employee";
  }

  if (plural && whitelabelSettings.employeeTermPlural) {
    return whitelabelSettings.employeeTermPlural;
  }

  if (!plural && whitelabelSettings.employeeTermSingular) {
    return whitelabelSettings.employeeTermSingular;
  }

  // Fallback to default
  return plural ? "Employees" : "Employee";
};

/**
 * Get lowercase version of employee terminology
 */
export const getEmployeeTerminologyLower = (
  whitelabelSettings: WhitelabelSettings | null,
  plural: boolean = false
): string => {
  return getEmployeeTerminology(whitelabelSettings, plural).toLowerCase();
};

/**
 * React hook to get employee terminology with whitelabel support
 * Returns an object with helper functions for getting singular/plural terms
 *
 * Usage:
 * const { employee, employees, employeeLower, employeesLower } = useEmployeeTerminology();
 */
export const useEmployeeTerminology = () => {
  const { whitelabelSettings } = useWhitelabel();

  return {
    employee: getEmployeeTerminology(whitelabelSettings, false),
    employees: getEmployeeTerminology(whitelabelSettings, true),
    employeeLower: getEmployeeTerminologyLower(whitelabelSettings, false),
    employeesLower: getEmployeeTerminologyLower(whitelabelSettings, true),
  };
};
